import { useState, useEffect } from 'react';
import { useLanguage } from '../../providers/preferenceProvider';
import { updateCompany, createCompany, deleteCompany } from '../../api/company-api';
import { getRoles } from '../../api/role-api';
import { useToast } from '../../providers/toastProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faUser } from '@fortawesome/free-solid-svg-icons';
import SearchableSelect from './searchableSelect';
import { useConfig } from '../../providers/configProvider';
import { useNavigate } from 'react-router-dom';
import '../../css/overlay.css';
import { ACL } from '../../acls';

const EditCompany = ({ isOpen, onClose, company, createNew, onRefresh }) => {
  const { t } = useLanguage();
  const { showToast, showLoading, hideLoading, showActionToast } = useToast();
  const { isAuthorized } = useConfig();
  const [name, setName] = useState('');
  const [maxUsers, setMaxUsers] = useState('');
  const [description, setDescription] = useState('');
  const [defaultRole, setDefaultRole] = useState(' ');
  const [fieldErrors, setFieldErrors] = useState([]);

  const [roleOptions, setRoleOptions] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;
    if (company) {
      setName(company.name);
      setMaxUsers(company.maxUsers);
      setDescription(company.description);
      setDefaultRole(company.defaultRoleId || '');
    }

    getRoles().then((response) => {
      setRoleOptions(
        response.data.map((role) => ({
          id: role.id,
          name: role.name
        }))
      );
    });
  }, [isOpen, company]);

  if (!isOpen) return null;

  const closeOverlay = () => {
    setName('');
    setMaxUsers('');
    setDescription('');
    setDefaultRole('');
    setFieldErrors([]);
    onClose();
  }

  const saveChange = () => {
    showLoading();

    const newErrors = [];
    if (!name || name.trim() === '') newErrors.push('name');
    if (!maxUsers || isNaN(maxUsers) || maxUsers <= 0) newErrors.push('maxUsers');
    if (!defaultRole || !roleOptions.some(r => r.id === defaultRole)) newErrors.push('role');

    setFieldErrors(newErrors);

    if (newErrors.length > 0) {
      hideLoading();
      showToast(t.toast.fill_all_fields, 'error');
      return;
    }

    if (createNew) {
      createCompany(name?.trim(), maxUsers, description, defaultRole).then(() => {
        if (onRefresh) onRefresh(true);
        hideLoading();
        showToast(t.company + " " + t.toast.created_success, 'success');
        closeOverlay();
      }).catch(() => {
        hideLoading();
      });
    } else {
      updateCompany(company.id, name?.trim(), maxUsers, description, defaultRole).then(() => {
        if (onRefresh) onRefresh(true);
        hideLoading();
        showToast(t.company + " " + t.toast.updated_success, 'success');
        closeOverlay();
      }).catch(() => {
        hideLoading();
      });
    }
  }

  const DeleteCompanyModal = ({ close, onConfirm }) => {
    const [selected, setSelected] = useState("keep");
    return (
      <div className="flex flex-col gap-2 mt-2">
        <div>
          <input  type="radio" id="deleteUsers" name="deleteUsers" value="delete" checked={selected === "delete"} onChange={() => setSelected("delete")} />
          <label htmlFor="deleteUsers">{t.delete_users_in_company}</label>
        </div>
        <div>
          <input type="radio" id="keepUsers" name="deleteUsers" value="keep" checked={selected === "keep"} onChange={() => setSelected("keep")} />
          <label htmlFor="keepUsers">{t.keep_users_in_company}</label>
        </div>
        <div className="action-content">
          <button className="cancel" onClick={close}>{t.close}</button>
          <button className="confirm" onClick={() => { close(); onConfirm(selected); }}>{t.confirm}</button>
        </div>
      </div>
    );
  };

  const deleteConfirmation = () => {
    showActionToast(
      t.toast.confirm_delete_company.replace("{ph_name}", company.name).replace("{ph_users}", company.userCount),
      null,
      (close) => <DeleteCompanyModal close={close} onConfirm={handleDeleteCompany} />
    );
  }

  const handleDeleteCompany = (deleteUsers) => {
    var shouldDeleteUsers = false;
    if (deleteUsers === "delete") shouldDeleteUsers = true;
    showLoading();
    deleteCompany(company.id, shouldDeleteUsers).then(() => {
      if (onRefresh) onRefresh(true);
      showToast(t.company + " " + t.toast.deleted_success, 'success');
      hideLoading();
      closeOverlay();
    }).catch(() => {
      hideLoading();
    });
  }

  const goToCompanyPage = () => {
    if (company && company.id) {
      navigate(`/company/${company.id}`);
    }
  };

  return (
    <div className="overlay-backdrop" onClick={onClose}>
      <div className="overlay-content" onClick={(e) => e.stopPropagation()}>
        <div className="overlay-header">{createNew ? t.add : t.edit} {t.company}</div>
        <div className="overlay-admin-form flex flex-col gap-4 p-4">
          <div className="flex flex-row items-center gap-4">
            <label htmlFor="name" className="w-32"><FontAwesomeIcon icon={faPen} /> {t.name}</label>
            <input id="name" name="name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t.name + "..."} className={"overlay-admin-input" + (fieldErrors.includes("name") ? " input-error" : "")} />
          </div>

          <div className="flex flex-row items-center gap-4">
            <label htmlFor="maxUsers" className="w-32"><FontAwesomeIcon icon={faUser} /> {t.max_users}</label>
            <input type="number" min="1" id="maxUsers" name="maxUsers" value={maxUsers} onChange={(e) => setMaxUsers(e.target.value)} placeholder={t.max_users + "..."} className={"overlay-admin-input" + (fieldErrors.includes("maxUsers") ? " input-error" : "")} />
          </div>

          <div className="flex flex-row items-center gap-4">
            <label htmlFor="description" className="w-32"><FontAwesomeIcon icon={faPen} /> {t.description}</label>
            <textarea id="description" name="description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="description..." className="overlay-admin-textarea" style={{ height: '10vh' }} />
          </div>

          <SearchableSelect label={t.default_role} icon="role" name="role" value={defaultRole} onChange={setDefaultRole} options={roleOptions} error={fieldErrors.includes("role")} />
        </div>
        <div className="flex flex-row gap-2 justify-between">
          <div className='flex flex-row gap-2 align-items-center'>
            {!createNew && (
              <>
                <button className="btn" onClick={deleteConfirmation} disabled={!isAuthorized(ACL.COMPANY)}>{t.delete + " " + t.company}</button>
                <button className='btn' onClick={goToCompanyPage} disabled={!isAuthorized(ACL.COMPANY)}>View Company</button>
              </>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <button className="btn-close" onClick={closeOverlay}>{t.close}</button>
            <button className="btn" onClick={saveChange}>{t.confirm}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCompany;